/**
 * Month-over-month burn rate + AR balance chart, under the history table.
 * Modeled on UsageChart.tsx (Admin/Usage) — same ChartContainer/BarChart
 * composition from @mono/ui-components, no recharts imported directly here.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Bar, BarChart, CartesianGrid, ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent, XAxis, YAxis } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { MaskedField } from '../../../lib/access/MaskedField';
import { centsToDollarString } from './money';
const chartConfig = {
    burnRate: {
        label: 'Burn rate',
        theme: {
            light: '#2a78d6',
            dark: '#3987e5'
        }
    },
    arBalance: {
        label: 'AR balance',
        theme: {
            light: '#1baf7a',
            dark: '#199e70'
        }
    }
};
const dollarTick = (v)=>`$${centsToDollarString(BigInt(Math.round(v * 100)))}`;
/**
 * Pure transform from the server's newest-first months into ascending
 * (oldest→newest) plot rows in dollars. A masked series is OMITTED from
 * every row entirely — never zeroed — so it never enters the bars, the
 * Y-axis domain, or the tooltip payload.
 */ export function buildChartRows(months, burnRateMasked, arBalanceMasked) {
    return [
        ...months
    ].reverse().map((m)=>({
            periodMonth: m.periodMonth,
            ...burnRateMasked ? {} : {
                burnRate: Number(m.burnRateCents) / 100
            },
            ...arBalanceMasked ? {} : {
                arBalance: Number(m.arBalanceCents) / 100
            }
        }));
}
export function FinancialHistoryChart({ months, burnRateMasked, arBalanceMasked, resource }) {
    // Defense-in-depth, same argument as the funding-consumed bar in
    // FinancialHealthTab.tsx: the server already zeroes masked fields, but the
    // chart must never derive bars/ticks from a value independent of the same
    // view.fieldMasked(...) predicate the placeholder below keys off.
    if (burnRateMasked && arBalanceMasked) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "rounded-lg border bg-card p-4",
            "data-testid": Testids.deliveryFinancial.healthHistoryChart,
            children: [
                /*#__PURE__*/ _jsx("span", {
                    className: "text-xs font-medium uppercase tracking-wide text-muted-foreground",
                    children: "Month over month"
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "mt-1 text-sm text-muted-foreground",
                    children: /*#__PURE__*/ _jsx(MaskedField, {
                        fieldName: "burn_rate_cents",
                        resource: resource,
                        children: /*#__PURE__*/ _jsx("span", {})
                    })
                })
            ]
        });
    }
    const rows = buildChartRows(months, burnRateMasked, arBalanceMasked);
    if (rows.length === 0) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "rounded-lg border bg-card p-4",
            "data-testid": Testids.deliveryFinancial.healthHistoryChart,
            children: [
                /*#__PURE__*/ _jsx("span", {
                    className: "text-xs font-medium uppercase tracking-wide text-muted-foreground",
                    children: "Month over month"
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "py-6 text-center text-sm text-muted-foreground",
                    children: "No month recorded yet"
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "rounded-lg border bg-card p-4",
        "data-testid": Testids.deliveryFinancial.healthHistoryChart,
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "text-xs font-medium uppercase tracking-wide text-muted-foreground",
                children: "Month over month"
            }),
            /*#__PURE__*/ _jsx(ChartContainer, {
                config: chartConfig,
                className: "mt-3 h-64 w-full",
                children: /*#__PURE__*/ _jsxs(BarChart, {
                    data: rows,
                    children: [
                        /*#__PURE__*/ _jsx(CartesianGrid, {
                            vertical: false
                        }),
                        /*#__PURE__*/ _jsx(XAxis, {
                            dataKey: "periodMonth",
                            tickLine: false,
                            axisLine: false,
                            tickMargin: 8
                        }),
                        /*#__PURE__*/ _jsx(YAxis, {
                            tickLine: false,
                            axisLine: false,
                            width: 64,
                            tickFormatter: dollarTick
                        }),
                        /*#__PURE__*/ _jsx(ChartTooltip, {
                            content: /*#__PURE__*/ _jsx(ChartTooltipContent, {})
                        }),
                        /*#__PURE__*/ _jsx(ChartLegend, {
                            content: /*#__PURE__*/ _jsx(ChartLegendContent, {})
                        }),
                        !burnRateMasked && /*#__PURE__*/ _jsx(Bar, {
                            dataKey: "burnRate",
                            fill: "var(--color-burnRate)",
                            radius: [
                                4,
                                4,
                                0,
                                0
                            ]
                        }),
                        !arBalanceMasked && /*#__PURE__*/ _jsx(Bar, {
                            dataKey: "arBalance",
                            fill: "var(--color-arBalance)",
                            radius: [
                                4,
                                4,
                                0,
                                0
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
